import { useRef } from 'react'
import Link from 'next/link'
import { useTranslation } from 'next-i18next'
import styled from 'styled-components'

import structure from '@/structure'
import { breakpoints, colors } from '@/styles'

import { SectionWrapper } from '../elements'
import DownloadSample from './download-sample'
import IconList from './icon-list'
import VideoSection from './video-section'
import { ReactComponent as AutomatedRentalPayments } from './icons/automated-rental-payments.svg'
import { ReactComponent as BackgroundCheck } from './icons/background-check.svg'
import { ReactComponent as CreateContractSVG } from './icons/create-contract.svg'
import { ReactComponent as CreditCardCheck } from './icons/credit-card-search.svg'
import { ReactComponent as FolderSVG } from './icons/folder.svg'
import { ReactComponent as MultiPlatformSharing } from './icons/multi-platform-sharing.svg'
import { ReactComponent as RocketSVG } from './icons/rocket.svg'
import { ReactComponent as SignLeasesSVG } from './icons/sign-leases.svg'
import { ReactComponent as UnlimitedListings } from './icons/unlimited-listings.svg'

const Wrapper = styled(SectionWrapper)`
  display: flex;
  flex-direction: column;
`

const ManagementWrapper = styled.div`
  > :not(:last-child) {
    margin-bottom: 100px;
  }

  @media (max-width: ${breakpoints.tabletLandscape}) {
    > :not(:last-child) {
      margin-bottom: 135px;
    }
  }

  @media (max-width: ${breakpoints.ipadMiniMax}) {
    > :not(:last-child) {
      margin-bottom: 120px;
    }
  }
`

const ManagementSectionWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  scroll-margin-top: ${structure.header.height}px;
  gap: 48px;

  :nth-of-type(even) {
    flex-direction: row-reverse;
  }

  @media (max-width: ${breakpoints.tabletLandscape}) {
    gap: 24px;
  }

  @media (max-width: ${breakpoints.ipadMiniMax}) {
    flex-direction: column;
    align-items: center;
    scroll-margin-top: ${structure.header.heightMobile}px;
    gap: 32px;

    :nth-of-type(even) {
      flex-direction: column;
    }
  }
`

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  flex: 1;
`

const SubTitle = styled.h2`
  font-family: Inter, sans-serif;
  font-weight: 700;
  font-size: 32px;
  line-height: 40px;
  margin: 0 0 32px 0;
  color: #111827;

  @media (max-width: ${breakpoints.ipadMiniMax}) {
    font-size: 24px;
    line-height: 32px;
    margin: 0 0 40px 0;
  }
`

const RentalMarketing = () => {
  const { t } = useTranslation('landlords')
  const marketingRef = useRef(null)
  const screeningRef = useRef(null)
  const contractsRef = useRef(null)

  const SECTIONS = [
    {
      videoSrc: '/videos/marketing.mp4',
      sectionRef: marketingRef,
      loop: false,
      imgAlt: 'rental marketing',
      id: 'rental-market',
      title: t('landlords:Rental Marketing'),
      backgroundColor: colors.purple96,
      listItems: [
        {
          title: t('landlords:Unlimited Listings'),
          content: t(
            'landlords:Post one, or 1000, listings for free with AI tools to optimize your flow.'
          ),
          icon: UnlimitedListings
        },
        {
          title: t('landlords:Multi-Platform Listing Sharing'),
          content: t(
            'landlords:Share your listing to Facebook & Craigslist in just a few clicks.'
          ),
          icon: MultiPlatformSharing
        },
        {
          title: t('landlords:Sponsored Listings'),
          content: (
            <>
              {t(
                'landlords:Stand out among thousands of listings with hundreds of thousands of monthly visits.'
              )}{' '}
              <Link href="/landlords/sponsored-listings">
                {t('landlords:Learn more about sponsored listings.')}
              </Link>
            </>
          ),
          icon: RocketSVG
        }
      ]
    },
    {
      videoSrc: '/videos/tenant-screening.mp4',
      loop: true,
      imgAlt: 'tenant screening report',
      sectionRef: screeningRef,
      id: 'screen-report',
      title: t('landlords:Tenant Screening Report'),
      backgroundColor: colors.red96,
      listItems: [
        {
          title: t('landlords:Court Records'),
          content: t(
            'landlords:Know who you’re renting to. Easily view records of criminal infractions and other red flags to identify trustworthy tenants quickly. '
          ),
          icon: FolderSVG
        },
        {
          title: t('landlords:Equifax Credit Checks'),
          content: t(
            'landlords:Make sure your new tenants can pay rent on time with an extensive analysis of their credit information, powered by Equifax®. '
          ),
          icon: CreditCardCheck
        },
        {
          title: t('landlords:Background Checks'),
          content: t(
            'landlords:Get a complete picture of applicants with information like a risk assessment, income-to-rent ratio, and income & employment verification. '
          ),
          icon: BackgroundCheck
        }
      ]
    },
    {
      id: 'contract-downloads',
      component: <DownloadSample key="contract-downloads" />
    },
    {
      videoSrc: '/videos/contracts-and-payments.mp4',
      imgAlt: 'contracts and payments',
      loop: false,
      sectionRef: contractsRef,
      id: 'contract-pay',
      title: t('landlords:Contracts & Payments'),
      backgroundColor: colors.green96,
      listItems: [
        {
          title: t('landlords:Create Contracts'),
          content: t(
            'landlords:Generate standard digital lease agreements in just a few clicks, or upgrade to take advantage of custom contracts & lease addendums. '
          ),
          icon: CreateContractSVG
        },
        {
          title: t('landlords:Sign Leases'),
          content: t(
            'landlords:Securely send & sign contracts directly on the liv.rent platform, protected by military-grade encryption and authentication for added protection. '
          ),
          icon: SignLeasesSVG
        },
        {
          title: t('landlords:Automated Rental Payment'),
          content: t(
            'landlords:Get paid on time via credit card, PAD (coming soon), UnionPay, and even Bitcoin.'
          ),
          icon: AutomatedRentalPayments
        }
      ]
    }
  ]

  return (
    <Wrapper>
      <ManagementWrapper>
        {SECTIONS.map(
          ({
            id,
            title,
            videoSrc,
            listItems,
            backgroundColor,
            sectionRef,
            loop,
            component
          }) => {
            if (component) return component
            return (
              <ManagementSectionWrapper key={id} id={`landlord-${id}-section`}>
                <VideoSection
                  videoSrc={videoSrc}
                  videoRef={sectionRef}
                  loop={loop}
                />
                <ContentWrapper>
                  <SubTitle>{title}</SubTitle>
                  <IconList
                    listItems={listItems}
                    backgroundColor={backgroundColor}
                  />
                </ContentWrapper>
              </ManagementSectionWrapper>
            )
          }
        )}
      </ManagementWrapper>
    </Wrapper>
  )
}

export default RentalMarketing
